import React, { useEffect, useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../design-system/theme';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${theme.colors.bgSecondary};
  font-family: ${theme.typography.fontFamily};
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: ${theme.sidebarWidth};
  display: flex;
  flex-direction: column;
  width: calc(100% - ${theme.sidebarWidth});
  max-width: 100%;
  overflow-x: hidden;

  @media (max-width: ${theme.breakpoints.md}) {
    margin-left: 0;
    width: 100%;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  width: 100%;
`;

const MobileMenuButton = styled.button`
  display: none;

  @media (max-width: ${theme.breakpoints.md}) {
    position: fixed;
    left: ${theme.spacing.md};
    bottom: ${theme.spacing.md};
    z-index: 120;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing.xs};
    background: ${theme.colors.primary};
    color: ${theme.colors.textInverse};
    border: none;
    border-radius: ${theme.borderRadius.full};
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    box-shadow: ${theme.shadows.md};
    font-weight: ${theme.typography.fontWeight.semibold};
  }
`;

const SidebarBackdrop = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible: boolean }>`
  display: none;

  @media (max-width: ${theme.breakpoints.md}) {
    display: ${props => (props.visible ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    border: none;
    background: rgba(0, 0, 0, 0.35);
    z-index: 90;
  }
`;

export const AdminLayout: React.FC = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const adminToken = localStorage.getItem('adminToken');
  const isLoginPage = location.pathname === '/admin/login';

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // If not logged in and not on login page, redirect to login
  if (!adminToken && !isLoginPage) {
    return <Navigate to="/admin/login" replace />;
  }

  // If logged in and on login page, redirect to dashboard
  if (adminToken && isLoginPage) {
    return <Navigate to="/admin" replace />;
  }

  // Show login page if on login route
  if (isLoginPage) {
    return <Outlet />;
  }

  return (
    <LayoutContainer>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <SidebarBackdrop
        aria-label="Close menu"
        visible={sidebarOpen}
        onClick={() => setSidebarOpen(false)}
      />
      <MainContent>
        <TopBar />
        <ContentArea>
          <Outlet />
        </ContentArea>
      </MainContent>
      <MobileMenuButton onClick={() => setSidebarOpen((prev) => !prev)}>
        {sidebarOpen ? 'Close' : 'Menu'}
      </MobileMenuButton>
    </LayoutContainer>
  );
};

export default AdminLayout;

